import { Box, styled, Tooltip, Typography } from 'opensolar-ui'
import React from 'react'

export interface RecommendationItem {
  title: string
  quantity: number
  manufacturer?: string
  headlineFigure?: {
    value: number
    unit: string
  }
  tooltipContent?: React.ReactNode
}

interface RecommendationsListProps {
  items: RecommendationItem[]
  selectedIndex: number | null
  onSelect: (index: number) => void
  maxDisplay?: number
}

const RecommendationsContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  marginTop: '12px',
  maxHeight: '300px',
  overflowY: 'auto',
})

const RecommendationItemContainer = styled('div')<{ isSelected?: boolean }>(({ isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '8px 12px',
  borderRadius: '10px',
  border: isSelected ? '2px solid #1751D0' : '1px solid rgba(23, 81, 208, 0.12)',
  backgroundColor: isSelected ? 'rgba(23, 81, 208, 0.05)' : 'rgba(255, 255, 255, 0.98)',
  boxShadow: isSelected
    ? '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)'
    : '0 2px 4px rgba(0, 0, 0, 0.04)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: '#1751D0',
    backgroundColor: 'rgba(23, 81, 208, 0.05)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
  },
}))

const QuantityWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '28px',
  borderRadius: '6px',
  background: 'linear-gradient(135deg, rgba(23, 81, 208, 0.1) 0%, rgba(60, 145, 230, 0.1) 100%)',
  flexShrink: 0,
})

const QuantityText = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#1751D0',
})

const HeadlineFigureWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  justifyContent: 'center',
  flexShrink: 0,
  gap: '2px',
})

const HeadlineFigureValue = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#1751D0',
  lineHeight: '18px',
})

const HeadlineFigureUnit = styled(Typography)({
  fontSize: '10px',
  fontWeight: 500,
  color: '#6B7280',
  lineHeight: '12px',
})

const RecommendationTitleContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
  gap: '2px',
})

const RecommendationTitle = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  background: 'linear-gradient(90deg, #1751D0 0.01%, #3C91E6 98.75%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  lineHeight: '18px',
})

const RecommendationManufacturer = styled(Typography)({
  fontSize: '11px',
  fontWeight: 400,
  color: '#6B7280',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  lineHeight: '14px',
})

export const RecommendationsList: React.FC<RecommendationsListProps> = ({
  items,
  selectedIndex,
  onSelect,
  maxDisplay,
}) => {
  if (items.length === 0) {
    return null
  }

  const displayedItems = maxDisplay ? items.slice(0, maxDisplay) : items

  return (
    <RecommendationsContainer>
      {displayedItems.map((item, index) => {
        const itemContent = (
          <RecommendationItemContainer isSelected={selectedIndex === index} onClick={() => onSelect(index)}>
            <QuantityWrapper>
              <QuantityText>{item.quantity}</QuantityText>
            </QuantityWrapper>
            <RecommendationTitleContainer>
              <RecommendationTitle>{item.title || `Option ${index + 1}`}</RecommendationTitle>
              {item.manufacturer && <RecommendationManufacturer>{item.manufacturer}</RecommendationManufacturer>}
            </RecommendationTitleContainer>
            {item.headlineFigure && (
              <HeadlineFigureWrapper>
                <HeadlineFigureValue>{item.headlineFigure.value.toFixed(1)}</HeadlineFigureValue>
                <HeadlineFigureUnit>{item.headlineFigure.unit}</HeadlineFigureUnit>
              </HeadlineFigureWrapper>
            )}
          </RecommendationItemContainer>
        )

        if (item.tooltipContent) {
          return (
            <Tooltip key={index} title={item.tooltipContent} placement="top">
              {itemContent}
            </Tooltip>
          )
        }

        return <React.Fragment key={index}>{itemContent}</React.Fragment>
      })}
    </RecommendationsContainer>
  )
}
